/* ══════════ Ladezustände ══════════
   Ein Baukasten statt dreißig Einzellösungen.

   Vorher stand an jeder ladenden Stelle ein „Lädt…" – eine Zeile Text, die
   nichts über die kommende Form verrät. Wenn dann die Daten eintreffen,
   springt das Layout von einer Zeile auf zehn Karten. Das ist der Grund für
   das unruhige Gefühl beim Laden, nicht die Ladezeit selbst.

   Deshalb bilden die Skeletons hier die ECHTE Struktur nach: gleiche Klassen,
   gleiche Abstände, gleiche Höhen. Wo möglich benutzen sie sogar dieselben
   Layout-Klassen wie der spätere Inhalt (`.fr-row`, `.deck-card`, `.sec-row`) –
   dann kann die Höhe gar nicht auseinanderlaufen, weil sie aus demselben CSS
   kommt.

   Drei Bausteine reichen für fast alles:
     linie()  – eine Textzeile
     kreis()  – Avatar, Icon, Punkt
     block()  – Fläche (Bild, Balken, Knopf)

   Alles ist reines HTML als Zeichenkette, passend zum Rest der App, die
   ebenfalls per innerHTML rendert. Skeletons tragen `aria-hidden`, damit
   Screenreader nicht Dutzende bedeutungslose Kästen vorlesen; die ladende
   Region bekommt stattdessen `aria-busy`. */

import { esc } from '../core/text.js';

/* ── Primitive ───────────────────────────────────────────────────────────
   `w`/`h` dürfen alles sein, was CSS versteht (px, %, ch, em). */
export const linie = (w = '100%', h = '.85em') =>
  `<span class="skel skel-line" style="width:${w};height:${h}"></span>`;
export const kreis = (d = '32px') =>
  `<span class="skel skel-circle" style="width:${d};height:${d}"></span>`;
export const block = (w = '100%', h = '1em', r = '10px') =>
  `<span class="skel skel-block" style="width:${w};height:${h};border-radius:${r}"></span>`;

/* Zufällig wirkende, aber stabile Zeilenlängen. Gleich lange Balken sehen
   nach Tabelle aus, echter Text ist ungleichmäßig. Kein Math.random(): sonst
   flackern die Breiten bei jedem erneuten Rendern. */
const BREITEN = [92, 74, 86, 64, 96, 70, 82, 60];
export const zeilenBreite = (i) => BREITEN[i % BREITEN.length] + '%';

/* Ein Skeleton-Wrapper. `aria-hidden`, weil der Inhalt bedeutungslos ist.
   Die Klasse `skel-wrap` blendet ihn sanft ein (siehe CSS): bei einer Antwort
   in 40 ms sieht man ihn gar nicht erst aufblitzen. */
export const wrap = (inhalt, cls = '') =>
  `<div class="skel-wrap ${cls}" aria-hidden="true">${inhalt}</div>`;

/* ── Zusammengesetzte Skeletons ──────────────────────────────────────────
   Jedes bildet genau eine echte Komponente nach. Wer den echten Inhalt
   ändert, sollte hier nachziehen – deshalb steht bei jedem, was er abbildet. */

/* Deck-Karte in der Community (`deckCardHTML` in social/community.js):
   Titel, Fach-Zeile, Zahlenreihe, Knopfreihe. */
export const skelDeckKarten = (n = 6) => wrap(
  Array.from({ length: n }, (_, i) => `<div class="deck-card skel-card">
    <div class="skel-stack">
      ${linie(zeilenBreite(i), '1.05em')}
      ${linie('45%', '.75em')}
      <div class="skel-row">${block('54px', '.8em')}${block('54px', '.8em')}${block('54px', '.8em')}</div>
      <div class="skel-row skel-row-end">${block('84px', '30px', '999px')}${block('64px', '30px', '999px')}</div>
    </div>
  </div>`).join(''), 'skel-deckgrid');

/* Personenzeile (`friendRow`): runder Avatar, Name, Knöpfe rechts.
   Bewusst mit der echten Klasse `fr-row` – die Höhe kommt dann aus dem
   gleichen CSS wie später der Inhalt. */
export const skelPersonen = (n = 3) => wrap(
  Array.from({ length: n }, (_, i) => `<div class="fr-row skel-flat">
    <span class="skel-row">${kreis('34px')}${linie(70 + (i % 3) * 22 + 'px', '.9em')}</span>
    <span class="skel-row">${block('78px', '28px', '999px')}</span>
  </div>`).join(''));

/* Geräte- und Ereigniszeile im Sicherheitsbereich (`.sec-row`). */
export const skelSicherheit = (n = 2) => wrap(
  Array.from({ length: n }, (_, i) => `<div class="sec-row skel-flat">
    <div class="skel-stack">${linie(120 + (i % 2) * 40 + 'px', '.95em')}${linie('62%', '.72em')}</div>
    ${block('84px', '28px', '999px')}
  </div>`).join(''));

/* Aktivitäts-Feed (`.feed-item`): Avatar, zwei Zeilen, Reaktionsknopf. */
export const skelFeed = (n = 4) => wrap(
  Array.from({ length: n }, (_, i) => `<div class="feed-item skel-flat">
    <div class="feed-av">${kreis('38px')}</div>
    <div class="feed-main skel-stack">${linie(zeilenBreite(i), '.92em')}${linie('30%', '.7em')}</div>
    ${block('52px', '28px', '999px')}
  </div>`).join(''));

/* Bestenliste: Treppchen oben, Zeilen darunter. Beides zusammen, weil die
   echte Liste ebenfalls immer beides zeigt. */
export const skelBestenliste = (n = 6) => wrap(
  `<div class="lb-podium">${[2, 1, 3].map((platz) => `<div class="lb-pod lb-pod-${platz} skel-flat">
      ${kreis('54px')}${linie('72%', '.9em')}${linie('46%', '.75em')}
    </div>`).join('')}</div>
   <div class="lb-list">${Array.from({ length: n }, (_, i) => `<div class="lb-row skel-flat">
      ${block('22px', '22px', '8px')}${kreis('34px')}
      <span class="skel-stack skel-grow">${linie(zeilenBreite(i), '.9em')}${linie('38%', '.7em')}</span>
      ${linie('58px', '.85em')}
    </div>`).join('')}</div>`);

/* Rangliste im Abenteuer (`.alb-row`) – schmaler als die große Bestenliste. */
export const skelRangliste = (n = 8) => wrap(
  Array.from({ length: n }, (_, i) => `<div class="alb-row skel-flat">
    ${block('26px', '20px', '8px')}${linie(zeilenBreite(i), '.9em')}${linie('84px', '.85em')}
  </div>`).join(''));

/* Duell-Zeilen (`.duel-row`): Rang, Name, Balken, Wert. */
export const skelDuell = (n = 4) => wrap(
  Array.from({ length: n }, (_, i) => `<div class="duel-row skel-flat">
    ${block('24px', '20px', '8px')}${linie('92px', '.9em')}
    <span class="duel-bar">${block(60 - i * 12 + '%', '10px', '999px')}</span>
    ${linie('34px', '.85em')}
  </div>`).join(''));

/* Öffentliches Profil im Popup: Kopf, Kennzahlen, Deck-Liste. */
export const skelProfilPopup = () => wrap(
  `<div class="pp-head">${kreis('64px')}
     <div class="pp-main skel-stack">${linie('58%', '1.1em')}${linie('40%', '.8em')}${linie('86%', '.8em')}</div>
   </div>
   <div class="pp-stats">${Array.from({ length: 4 }, () => `<div class="pp-stat skel-stack skel-mid">
      ${linie('42px', '1.15em')}${linie('54px', '.72em')}</div>`).join('')}</div>
   <h4 class="dd-h">${linie('150px', '.95em')}</h4>
   <div class="pp-decks">${Array.from({ length: 2 }, (_, i) => `<div class="pp-deck skel-stack">
      ${linie(zeilenBreite(i), '.95em')}${linie('46%', '.72em')}</div>`).join('')}</div>`);

/* Gemeinsame Decks (`.collab-card`). */
export const skelCollab = (n = 2) => wrap(
  Array.from({ length: n }, (_, i) => `<div class="collab-card skel-flat skel-stack">
    ${linie(zeilenBreite(i), '1em')}${linie('52%', '.75em')}
    <div class="skel-row">${block('150px', '28px', '999px')}${block('160px', '28px', '999px')}</div>
  </div>`).join(''));

/* Eigene veröffentlichte Decks im Profil (`.prof-deck`). */
export const skelMeineDecks = (n = 2) => wrap(
  `<div class="prof-deck-list">${Array.from({ length: n }, (_, i) => `<div class="prof-deck skel-stack">
      ${linie(zeilenBreite(i), '1em')}${linie('44%', '.72em')}</div>`).join('')}</div>`);

/* Deck-Vorschau im Popup: Fach-Marke, Zusammenfassung, Kartenpaare,
   Bewertungen. Bildet `deckDetailModal` nach. */
export const skelDeckVorschau = () => wrap(
  `<div class="skel-stack">${block('92px', '22px', '999px')}${linie('72%', '.85em')}${linie('46%', '.85em')}</div>
   <h4 class="dd-h">${linie('130px', '.95em')}</h4>
   <div class="dd-cards">${Array.from({ length: 3 }, (_, i) => `<div class="dd-card skel-flat skel-stack">
      ${linie(zeilenBreite(i), '.9em')}${linie(zeilenBreite(i + 3), '.9em')}</div>`).join('')}</div>
   <h4 class="dd-h">${linie('118px', '.95em')}</h4>
   <div class="dd-reviews">${Array.from({ length: 2 }, (_, i) => `<div class="dd-review skel-stack">
      ${linie('120px', '.85em')}${linie(zeilenBreite(i + 1), '.75em')}</div>`).join('')}</div>`);

/* Communitys (`.comm-row`). */
export const skelCommunitys = (n = 3) => wrap(
  Array.from({ length: n }, (_, i) => `<div class="comm-row skel-flat">
    ${kreis('36px')}
    <div class="skel-stack skel-grow">${linie(zeilenBreite(i), '.95em')}${linie('40%', '.72em')}</div>
    ${block('96px', '28px', '999px')}
  </div>`).join(''));

/* ── Fehler statt endloser Skeletons ─────────────────────────────────────
   Ein Skeleton, das nie verschwindet, ist die schlechteste aller Varianten:
   es verspricht Inhalt, der nie kommt. Jede ladende Stelle bekommt deshalb
   einen echten Fehlerzustand mit einem Weg zurück. */
export function fehlerBox(text, mitErneut = true) {
  return `<div class="load-error" role="alert">
    <span class="load-error-ico" aria-hidden="true">⚠</span>
    <div class="load-error-txt">${esc(text || 'Das konnte nicht geladen werden.')}</div>
    ${mitErneut ? '<button type="button" class="btn-ghost outline tiny" data-erneut>Erneut versuchen</button>' : ''}
  </div>`;
}
/* Hängt den „Erneut versuchen"-Knopf an. Getrennt vom HTML, weil die
   aufrufende Stelle weiß, WAS zu wiederholen ist. */
export function wireErneut(host, fn) {
  const b = host && host.querySelector('[data-erneut]');
  if (b) b.onclick = fn;
}

/* Region als ladend markieren – für Screenreader und für CSS-Haken.
   `aria-busy` ist die dokumentierte Art, „hier kommt noch etwas" zu sagen,
   ohne den Inhalt vorzulesen. */
export function ladend(el, an) {
  if (!el) return;
  if (an) el.setAttribute('aria-busy', 'true');
  else el.removeAttribute('aria-busy');
}

/* Skeleton in einen Container setzen und ihn als ladend markieren. */
export function zeigeSkeleton(el, html) {
  if (!el) return;
  ladend(el, true);
  el.innerHTML = html;
}
/* Ergebnis einsetzen und die Ladekennzeichnung wieder entfernen. */
export function zeigeInhalt(el, html) {
  if (!el) return;
  el.innerHTML = html;
  ladend(el, false);
}
